import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Briefcase, Building2, Plus } from 'lucide-react'
import AppShell from '../../components/b2b/AppShell'
import { api, APIError, extractErrorMessage } from '../../api/client'

export default function CompaniesScreen() {
  const navigate = useNavigate()
  const [companies, setCompanies] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    let cancelled = false
    api.get('/api/b2b/companies')
      .then((data) => { if (!cancelled) setCompanies(data) })
      .catch(() => { if (!cancelled) setError('Could not load companies.') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  return (
    <AppShell>
      <div className="px-6 md:px-10 py-8 md:py-12 max-w-5xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-ink-1">Companies</h1>
            <p className="text-sm text-ink-3 mt-1">The organizations you open jobs for.</p>
          </div>
          <button
            type="button"
            onClick={() => setCreating((v) => !v)}
            className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-5 min-h-[44px] rounded-2xl transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
          >
            <Plus className="w-4 h-4" />
            New company
          </button>
        </div>

        {error && (
          <div className="mt-6 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>
        )}

        {creating && (
          <CreateCompanyForm
            onCancel={() => setCreating(false)}
            onCreated={(company) => navigate(`/b2b/companies/${company.id}`)}
          />
        )}

        <div className="mt-8">
          {loading ? (
            <p className="text-sm text-ink-3">Loading…</p>
          ) : companies.length === 0 ? (
            <EmptyState onCreate={() => setCreating(true)} />
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {companies.map((c) => <CompanyCard key={c.id} company={c} />)}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  )
}

function CompanyCard({ company }) {
  return (
    <Link
      to={`/b2b/companies/${company.id}`}
      className="bg-white rounded-2xl shadow-card hover:shadow-card-hover p-5 flex items-center gap-4 transition"
    >
      <div className="w-10 h-10 rounded-xl bg-accent/10 text-accent flex items-center justify-center flex-shrink-0">
        <Building2 className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <h2 className="text-base font-bold text-ink-1 truncate">{company.name}</h2>
        {company.description && (
          <p className="text-xs text-ink-3 mt-1 line-clamp-1">{company.description}</p>
        )}
      </div>
      <div className="flex items-center gap-2 text-ink-3 text-sm">
        <Briefcase className="w-4 h-4" />
        <span>{company.job_count}</span>
      </div>
    </Link>
  )
}

function EmptyState({ onCreate }) {
  return (
    <div className="bg-white rounded-3xl shadow-card p-12 text-center">
      <div className="w-12 h-12 rounded-2xl bg-accent/10 text-accent flex items-center justify-center mx-auto">
        <Building2 className="w-5 h-5" />
      </div>
      <h2 className="mt-4 text-lg font-bold text-ink-1">No companies yet</h2>
      <p className="mt-1 text-sm text-ink-3 max-w-sm mx-auto">
        Add the organizations you hire for, then link jobs to them.
      </p>
      <button
        type="button"
        onClick={onCreate}
        className="mt-6 inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-5 py-2.5 rounded-2xl transition"
      >
        <Plus className="w-4 h-4" />
        New company
      </button>
    </div>
  )
}

function CreateCompanyForm({ onCancel, onCreated }) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const handleCreate = async () => {
    setError('')
    if (!name.trim()) { setError('Name is required.'); return }
    setSaving(true)
    try {
      const created = await api.post('/api/b2b/companies', {
        name: name.trim(),
        description: description.trim(),
      })
      onCreated(created)
    } catch (err) {
      setError(err instanceof APIError ? extractErrorMessage(err.data) : 'Could not create company.')
      setSaving(false)
    }
  }

  return (
    <div className="mt-6 bg-white rounded-3xl shadow-card border border-black/5 p-6 space-y-4">
      {error && (
        <div className="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>
      )}
      <div className="space-y-1.5">
        <label className="text-xs font-bold text-ink-2 uppercase tracking-wide ml-1">Company name</label>
        <div className="flex items-center gap-3 rounded-2xl border border-black/10 bg-white px-4 py-3.5 focus-within:border-accent transition-colors">
          <input
            type="text"
            placeholder="e.g., Acme Inc."
            className="flex-1 text-[15px] outline-none bg-transparent"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={saving}
            autoFocus
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <label className="text-xs font-bold text-ink-2 uppercase tracking-wide ml-1">Description</label>
        <textarea
          rows={3}
          placeholder="What this company does, hiring context, anything useful…"
          className="w-full text-[15px] outline-none bg-white rounded-2xl border border-black/10 px-4 py-3.5 focus:border-accent transition-colors resize-y"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={saving}
        />
      </div>
      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="px-5 py-2.5 rounded-2xl text-sm font-semibold text-ink-2 hover:bg-canvas transition disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleCreate}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-5 py-2.5 rounded-2xl transition disabled:opacity-50"
        >
          {saving ? 'Creating…' : 'Create company'}
        </button>
      </div>
    </div>
  )
}
